import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
  catchError,
  concatAll,
  concatMap,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import * as ChatActions from './chat.actions';
import * as ChatSelector from './chat.selectors';
import * as fromChat from './chat.reducer';

import { ChatService } from './chat.service';
import { combineLatest, from, of } from 'rxjs';
import { APIService } from '../../services/api/api-service';

@Injectable()
export class ChatEffects {
  constructor(
    private actions$: Actions,
    private store: Store<fromChat.State>,
    private apiService: APIService,
    private notification: NzNotificationService,
    private readonly chatService: ChatService
  ) {}

  startEncounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.startEncounter),
      mergeMap(({ appointmentId }) =>
        from(this.apiService.joinChat(appointmentId)).pipe(
          // mergeMap(response => {
          //   if (!response) {
          //     throw new Error('Chat not found')
          //   }
          // return from(this.chatService.initClient(response?.auth?.token, response.chatId)).pipe(
          map(authResponse => {
            this.chatService.initClient(authResponse?.auth?.token);
            return ChatActions.startEncounterSuccess({
              response: authResponse
            });
          }),
          // );
          // }),
          catchError(error => {
            this.notification.error(
              'Error',
              'There was an error starting encounter chat.'
            );
            return of(ChatActions.startEncounterFailure({ error }));
          })
        )
      )
    )
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.refreshToken),
      withLatestFrom(this.store.select(ChatSelector.getAuth)),
      switchMap(([action, auth]) => {
        if (!auth) {
          return of(
            ChatActions.refreshTokenFailure({
              error: new Error('Chat Auth Token is missing')
            })
          );
        }
        return this.apiService.refreshToken(auth.token).pipe(
          map(auth => ChatActions.refreshTokenSuccess({ auth })),
          catchError(error => {
            console.error('ERROR', error);
            return of(ChatActions.refreshTokenFailure({ error }));
          })
        );
      })
    )
  );

  refreshTokenSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.refreshTokenSuccess),
        switchMap(({ auth }) => this.chatService.setNewToken(auth.token))
      ),
    { dispatch: false }
  );

  connectionStateChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.connectionStateChanged),
      withLatestFrom(this.store.select(ChatSelector.getAuth)),
      map(([{ connectionState }, auth]) => {
        if (connectionState === 'disconnecting' && auth) {
          return ChatActions.refreshToken();
        } else if (connectionState === 'connected') {
          return ChatActions.loadMessages();
          // return ChatActions.nullAction();
        } else {
          return ChatActions.nullAction();
        }
      })
    )
  );

  loadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.loadMessages),
      withLatestFrom(this.store.select(ChatSelector.getChatId)),
      mergeMap(([action, chatId]) =>
        from(this.chatService.loadMessages(chatId)).pipe(
          map(messages => {
            return ChatActions.loadMessagesSuccess({ messages });
          })
        )
      )
    )
  );

  loadMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.loadMore),
      withLatestFrom(this.store.select(ChatSelector.getChatState)),
      mergeMap(([action, chatState]) => {
        return from(
          this.chatService.loadMessages(chatState.chatId, {
            anchor: chatState.messages.length
          })
        ).pipe(
          map(messages => {
            return ChatActions.loadMoreSuccess({
              messages
            });
          })
        );
      })
    )
  );

  closeChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.closeChat),
        map(() => {
          this.chatService.closeConnection();
        })
      ),
    { dispatch: false }
  );

  sendMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.sendMessage),
        withLatestFrom(this.store.select(ChatSelector.getChatId)),
        switchMap(([action, chatId]) =>
          from(this.chatService.sendMessage(chatId, action.message, action.files)).pipe(
            // this will trigger messageAdded event
            // map(() => ChatActions.messageAdded({ chatId, message: action.message })),
            catchError(error => {
              console.error(error);
              this.notification.error(
                'Uh oh!',
                'Failed to send message, please try again'
              );
              return of(null);
            })
          )
        )
      ),
    { dispatch: false }
  );

  joinVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.joinVideo),
      map(({joinUrl}) => {
        return ChatActions.joinVideoSuccess({videoRoomId: joinUrl}); 
      })
      // withLatestFrom(
      //   combineLatest([
      //     this.store.select(ChatSelector.getAuth),
      //     this.store.select(ChatSelector.getChatId)
      //   ])
      // ),
      // switchMap(([action, [auth, chatId]]) => {
      //   return from(this.chatService.joinVideoRoom(auth, chatId)).pipe(
      //     map(res => ChatActions.joinVideoSuccess({ videoRoomId: res.sid })),
      //     catchError(error => of(ChatActions.joinVideoFailure({ error })))
      //   );
      // })
    )
  );

  joinVideoFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.joinVideoFailure),
        tap(({ error }) => {
          console.error(error);
          this.notification.error(
            'Uh oh!',
            error.message || 'Failed to join video'
          );
        })
      ),
    { dispatch: false }
  );

  // leaveRoom$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(ChatActions.leaveRoom),
  //     mergeMap(() =>
  //       from(this.chatService.leaveRoom()).pipe(
  //         map(() => ChatActions.leaveRoomSuccess()),
  //         catchError(error => of(ChatActions.leaveRoomFailure({ error })))
  //       )
  //     )
  //   )
  // );
}
